// src/components/Result.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import styles from "../css/Result.module.css";
import Footer from "./Footer";

// Fallback image if logo fails to load
const fallbackLogo = "/images/logo-placeholder.png";

// Component to lazy-load matches for a league using IntersectionObserver
const LeagueGroup = ({ league, matches, userTimeZone }) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "200px" }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={styles.leagueGroup}>
      <div className={styles.leagueHeader}>
        <img
          src={league.logo}
          alt={league.name}
          className={styles.leagueLogo}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = fallbackLogo;
          }}
          loading="lazy"
        />
        <span className={styles.leagueName}>{league.name}</span>
      </div>
      {visible ? (
        <ul className={styles.list}>
          {matches.map((match) => (
            <li key={match.fixture.id} className={styles.matchItem}>
              <div className={styles.date}>
                {new Date(match.fixture.date).toLocaleString(undefined, {
                  timeZone: userTimeZone,
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              <div className={styles.scoreLine}>
                <div className={styles.teamLeft}>
                  <img
                    src={match.teams.home.logo}
                    alt={match.teams.home.name}
                    className={styles.teamLogo}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = fallbackLogo;
                    }}
                    loading="lazy"
                  />
                  <div className={styles.teamInfoBlock}>
                    <span className={styles.teamName}>
                      {match.teams.home.name}
                    </span>
                    {match.teams.home.winner && (
                      <span className={styles.winnerBadge}>WON</span>
                    )}
                  </div>
                </div>
                <div className={styles.scoreMiddle}>
                  {match.goals.home}-{match.goals.away}
                </div>
                <div className={styles.teamRight}>
                  <div className={styles.teamInfoBlock}>
                    {match.teams.away.winner && (
                      <span className={styles.winnerBadge}>WON</span>
                    )}
                    <span className={styles.teamName}>
                      {match.teams.away.name}
                    </span>
                  </div>
                  <img
                    src={match.teams.away.logo}
                    alt={match.teams.away.name}
                    className={styles.teamLogo}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = fallbackLogo;
                    }}
                    loading="lazy"
                  />
                </div>
              </div>
              <div className={styles.status}>
                Status: {match.fixture.status.long}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles.placeholder}>Loading matches...</div>
      )}
    </div>
  );
};

const Result = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    let isMounted = true;
    const maxRetries = 3;
    let retryCount = 0;

    const fetchData = () => {
      axios
        .get(
          "https://fetchrss.visiontrek.io/api-football/recent-results?days=2"
        )
        .then((resp) => {
          if (isMounted) {
            setData(resp.data);
            setError(null);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (isMounted) {
            if (retryCount < maxRetries) {
              retryCount += 1;
              setTimeout(fetchData, 1000);
            } else {
              setError(err);
              setLoading(false);
            }
          }
        });
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading)
    return (
      <div className={styles.loadingWrapper}>
        <div className={styles.loader}></div>
      </div>
    );
  if (error)
    return (
      <div className={styles.error}>
        Error fetching results. Retried 3 times.
      </div>
    );

  // Filter by search term
  const filtered = data.results.filter((match) => {
    const term = searchTerm.toLowerCase();
    return (
      match.league.name.toLowerCase().includes(term) ||
      match.teams.home.name.toLowerCase().includes(term) ||
      match.teams.away.name.toLowerCase().includes(term)
    );
  });

  // Sort by date
  filtered.sort((a, b) => {
    const diff = new Date(a.fixture.date) - new Date(b.fixture.date);
    return sortOrder === "asc" ? diff : -diff;
  });

  // Group filtered + sorted matches
  const grouped = filtered.reduce((acc, match) => {
    const id = match.league.id;
    if (!acc[id]) acc[id] = { league: match.league, matches: [] };
    acc[id].matches.push(match);
    return acc;
  }, {});

  return (
    <div className={styles.wrapper}>
      <div className={styles.controls}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by league or team"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className={styles.clearButton}
              onClick={() => setSearchTerm("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <select
          className={styles.sortSelect}
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="desc">Newest First</option>
          <option value="asc">Oldest First</option>
        </select>
      </div>
      <div className={styles.range}>
        <strong>From:</strong>{" "}
        {new Date(data.from).toLocaleString(undefined, {
          timeZone: userTimeZone,
        })}
        <strong>To:</strong>{" "}
        {new Date(data.to).toLocaleString(undefined, {
          timeZone: userTimeZone,
        })}
      </div>
      {Object.values(grouped).map(({ league, matches }) => (
        <LeagueGroup
          key={league.id}
          league={league}
          matches={matches}
          userTimeZone={userTimeZone}
        />
      ))}
      <Footer />
    </div>
  );
};

export default Result;
